/**
 * frontend/src/components/ProfileSelector.ts
 * ============================================
 * Selector compacto de perfil de mapeo.
 * Vive bajo cada FileDropZone en la pantalla de Upload.
 *
 * UI:
 *   [selector: — Elegí un perfil — ▼]  [+]  [Editar]  [Ver todos (N)]
 *
 * Elegir un perfil es obligatorio: sin él no se puede saber qué cuenta conciliar
 * ni cómo interpretar los importes, y adivinarlo produce resultados verosímiles
 * pero incorrectos.
 *
 * Sin archivo, la fila queda reducida al acceso a la lista de perfiles:
 * importar, exportar y eliminar no dependen de tener un archivo en la mano.
 *
 * Callbacks:
 *   onProfileSelected(profile | null)  → notifica al padre qué perfil está activo
 *
 * El padre lee `seleccion` al iniciar la conciliación para saber qué enviarle
 * al backend: el id de un perfil guardado, o el borrador completo si el
 * usuario eligió "usar una vez" sin guardarlo.
 */

import { listarPerfiles } from "../api";
import type {
  ColumnProfile,
  ColumnProfileResponse,
  SeleccionPerfil,
  TipoFuente,
} from "../types";
import { MappingWizardModal } from "./MappingWizardModal";
import { ProfilesManagerModal } from "./ProfilesManagerModal";
import { icon } from "../utils/icons";
import { ProfilePicker } from "./ProfilePicker";

interface ProfileSelectorOptions {
  tipoFuente: TipoFuente;
  onProfileSelected: (profile: Partial<ColumnProfile> | null) => void;
}

export class ProfileSelector {
  element: HTMLElement;
  private _opts: ProfileSelectorOptions;
  private _perfiles: ColumnProfileResponse[] = [];
  private _selectedId: string | null = null;
  private _archivo: File | null = null;
  /** Borrador del wizard usado una sola vez (sin guardar en el store). */
  private _borrador: Partial<ColumnProfile> | null = null;
  private _picker: ProfilePicker | null = null;

  constructor(opts: ProfileSelectorOptions) {
    this._opts = opts;
    this.element = document.createElement("div");
    this.element.className = "profile-selector";
    this._renderVacio();
    this._cargarPerfiles();
  }

  /** Llamado por UploadView cuando el usuario sube un archivo */
  setArchivo(file: File | null): void {
    this._archivo = file;
    this._renderConArchivo();
  }

  private async _cargarPerfiles(): Promise<void> {
    try {
      this._perfiles = await listarPerfiles(this._opts.tipoFuente);
      // El perfil elegido pudo eliminarse desde la lista: seguir tomándolo
      // como elegido dejaría un Editar sin destino y conciliaría con un mapeo
      // que ya no existe.
      if (this._selectedId && !this._perfiles.some(p => p.id === this._selectedId)) {
        this._selectedId = null;
        this._opts.onProfileSelected(null);
      }
      this._render();
    } catch {
      // Si falla la carga de perfiles, mostrar selector sin opciones guardadas
      this._render();
    }
  }

  /**
   * Sin archivo: solo el acceso a la lista, en el mismo lugar que después.
   *
   * La fila no lleva `data-recorrido="perfil"`: el recorrido espera a que
   * aparezca para saber que el archivo ya se cargó.
   */
  private _renderVacio(): void {
    this._picker?.destroy();
    this._picker = null;
    this.element.hidden = false;
    this.element.innerHTML = `
      <div class="profile-selector__row">
        <span class="profile-selector__nota">Cargue el archivo para elegir su perfil.</span>
        ${this._botonGestor()}
      </div>`;
    this.element.querySelector("#ps-ver")!.addEventListener("click", () => this.abrirGestor());
  }

  /** Con perfiles, la lista; sin ninguno, lo único que se puede hacer es importar. */
  private _botonGestor(): string {
    const count = this._perfiles.length;
    return `<button class="btn btn--secondary btn--sm" id="ps-ver" type="button">${
      count > 0 ? `Ver todos (${count})` : `${icon("upload")} Importar perfiles`}</button>`;
  }

  private _renderConArchivo(): void {
    this._render();
  }

  private _render(): void {
    this._picker?.destroy();
    this._picker = null;
    if (!this._archivo) {
      this._renderVacio();
      return;
    }

    this.element.hidden = false;

    this.element.innerHTML = `
      <div class="profile-selector__row" data-recorrido="perfil">
        <div class="profile-selector__select" id="ps-picker-${this._opts.tipoFuente}"></div>
        <button class="btn btn--secondary profile-selector__create" id="ps-nuevo" type="button" aria-label="Crear nuevo perfil" data-tooltip="Nuevo perfil">${icon("plus")}</button>
        ${this._selectedId ? `<button class="btn btn--secondary btn--sm" id="ps-editar" type="button">${icon("edit")} Editar</button>` : ""}
        ${this._botonGestor()}
      </div>`;

    // ── Listeners ─────────────────────────────────────────────────────────

    this._picker = new ProfilePicker(this.element.querySelector<HTMLElement>(".profile-selector__select")!, {
      id: `ps-select-${this._opts.tipoFuente}`,
      label: `Perfil de ${this._opts.tipoFuente === "extracto" ? "extracto" : "libro mayor"}`,
      profiles: this._perfiles, selectedId: this._selectedId, draft: this._borrador !== null,
      onSelect: id => {
        // Elegir del dropdown descarta cualquier borrador previo.
        this._borrador = null;
        this._selectedId = id;
        const perfil = this._perfiles.find(p => p.id === this._selectedId);
        this._opts.onProfileSelected(perfil ? perfil.detalle : null);
        this._render();
        this.element.querySelector<HTMLButtonElement>(`#ps-select-${this._opts.tipoFuente}`)?.focus();
      },
    });

    this.element.querySelector("#ps-nuevo")!.addEventListener("click", () => {
      if (!this._archivo) return;
      new MappingWizardModal({
        archivo:    this._archivo,
        tipoFuente: this._opts.tipoFuente,
        onSave: (saved) => {
          // Agregar al dropdown y seleccionar automáticamente
          this._perfiles.push(saved);
          this._selectedId = saved.id;
          this._borrador = null;
          this._opts.onProfileSelected(saved.detalle);
          this._render();
        },
        onUseOnce: (draft) => {
          // Sin guardar: el borrador viaja completo al backend en la conciliación.
          this._borrador = draft;
          this._selectedId = null;
          this._opts.onProfileSelected(draft);
          this._render();
        },
        onCancel: () => {},
      });
    });

    this.element.querySelector("#ps-editar")?.addEventListener("click", () => {
      const perfil = this._perfiles.find(p => p.id === this._selectedId);
      if (perfil) this._editarPerfil(perfil, "ps-editar");
    });
    this.element.querySelector("#ps-ver")?.addEventListener("click", () => this.abrirGestor());
  }

  /**
   * Abre la lista de perfiles de este tipo: importar, exportar y eliminar.
   *
   * No necesita un archivo cargado: quien abre la aplicación por primera vez
   * tiene que poder importar sus perfiles antes de cargar nada. Editar sí lo
   * necesita —el wizard verifica contra datos reales—, y el gestor lo
   * deshabilita mientras no lo haya.
   */
  private abrirGestor(): void {
    new ProfilesManagerModal({
      tipoFuente: this._opts.tipoFuente,
      hayArchivo: this._archivo !== null,
      onEditar:   (perfil) => this._editarPerfil(perfil),
      // Recarga la lista: se pudo importar o eliminar un perfil.
      onClose: () => this._cargarPerfiles(),
    });
  }

  /**
   * Abre el wizard con un perfil guardado cargado, para corregirlo.
   *
   * Reusa el mismo wizard que la creación —incluida la verificación
   * obligatoria contra el archivo— porque editar es tan riesgoso como crear:
   * cambiar una columna sin comprobar produce una conciliación plausible y
   * equivocada. Al guardar hace upsert por id, así que no aparece un duplicado.
   */
  private _editarPerfil(perfil: ColumnProfileResponse, retorno = "ps-ver"): void {
    if (!this._archivo) return;

    new MappingWizardModal({
      archivo:         this._archivo,
      tipoFuente:      this._opts.tipoFuente,
      perfilExistente: perfil,
      onSave: (guardado) => {
        // Reemplazar en el listado: el id es el mismo, cambió el contenido.
        const i = this._perfiles.findIndex(p => p.id === guardado.id);
        if (i >= 0) this._perfiles[i] = guardado;
        else this._perfiles.push(guardado);

        // Si el perfil editado era el elegido, propagar el mapeo nuevo: dejar
        // el viejo seleccionado conciliaría con lo que el usuario acaba de
        // corregir.
        if (this._selectedId === guardado.id) {
          this._opts.onProfileSelected(guardado.detalle);
        }
        this._render();
        this.element.querySelector<HTMLButtonElement>(`#${retorno}`)?.focus();
      },
      onUseOnce: () => {},   // no se ofrece en modo edición
      onCancel:  () => { this.element.querySelector<HTMLButtonElement>(`#${retorno}`)?.focus(); },
    });
  }

  /**
   * Qué mapeo aplicar a este archivo, o null si todavía no eligió ninguno.
   *
   * `UploadView` lo usa para decidir si habilita el botón de conciliar: null
   * significa "falta configurar", no "que el backend se las arregle".
   */
  get seleccion(): SeleccionPerfil | null {
    const guardado = this._perfiles.find(p => p.id === this._selectedId);
    // Se manda el id, no el nombre: dos perfiles pueden llamarse igual y el
    // backend no tendría forma de saber a cuál se refiere.
    if (guardado) return { tipo: "guardado", id: guardado.id, nombre: guardado.nombre };
    if (this._borrador) return { tipo: "inline", perfil: this._borrador };
    return null;
  }
}
